// src/pages/FeedbackPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchFeedbacks,
    markFeedbackRead,
    deleteFeedback,
    clearError,
} from 'store/slices/feedbackSlice';
import { Star, Trash2, MoreHorizontal, Loader2, MessageSquare, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { format } from 'date-fns';
import useCanteenSocket from 'hooks/useCanteenSocket';
import { formatDateTime } from 'utlis/formateDateTime';

const FeedbackPage = () => {
    useCanteenSocket();
    usePermissionSocket();

    const dispatch = useDispatch();
    const { feedbacks, pagination, isLoading, error } = useSelector((state) => state.feedback);
    const { canRead, canUpdate, canDelete } = usePermissions('break_time_log');

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [currentFeedback, setCurrentFeedback] = useState(null);

    // FeedbackPage.jsx — FIXED DEBOUNCE
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
        }, 400);

        return () => clearTimeout(timer); // ← THIS FIXES EVERYTHING
    }, [searchTerm]);

    const refetch = useCallback(() => {
        if (canRead) {
            dispatch(fetchFeedbacks({ page, pageSize, search: searchTerm }));
        }
    }, [dispatch, page, pageSize, searchTerm, canRead]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleMarkRead = async (id, is_read) => {
        await dispatch(markFeedbackRead({ id, is_read })).unwrap();
        refetch();
        toast.success(is_read ? 'Marked as read' : 'Marked as unread');
    };

    const handleDelete = async () => {
        if (!currentFeedback) return;
        await dispatch(deleteFeedback(currentFeedback.id)).unwrap();
        toast.success('Feedback deleted');
        setDeleteOpen(false);
        refetch();
    };

    const columns = [

        {
            accessorKey: 'order_code',
            header: 'Order No',
            cell: ({ row }) => (
                <div className="max-w-xs truncate">{row.original.order_code || '-'}</div>
            ),
        },
        {
            accessorKey: 'created_by_name',
            header: 'Order By',
            cell: ({ row }) => row.original.created_by_name || '-',
        },
        {
            accessorKey: 'rating',
            header: 'Rating',
            cell: ({ row }) => (
                <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                        <Star
                            key={i}
                            className={`h-4 w-4 ${i < row.original.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                        />
                    ))}
                </div>
            ),
        },
        {
            accessorKey: 'comment',
            header: 'Comments',
            cell: ({ row }) => (
                <div className="max-w-xs truncate">{row.original.comment || '-'}</div>
            ),
        },
        {
            accessorKey: 'created_at',
            header: 'Submitted On',
            cell: ({ row }) => (formatDateTime(row.original.created_at) ?? '-'),
        },
        {
            accessorKey: 'is_read',
            header: 'Status',
            cell: ({ row }) => (
                <Badge variant={row.original.is_read ? 'secondary' : 'default'}>
                    {row.original.is_read ? 'Read' : 'Unread'}
                </Badge>
            ),
        },
        {
            accessorKey: 'updated_by_name',
            header: 'Read By',
            cell: ({ row }) => row.original.updated_by_name || '-',
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const fb = row.original;
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {!fb.is_read && canUpdate && (
                                <DropdownMenuItem onClick={() => handleMarkRead(fb.id, true)}>
                                    <MessageSquare className="mr-2 h-4 w-4" /> Mark as Read
                                </DropdownMenuItem>
                            )}
                            {/* {fb.is_read && canUpdate && (
                                <DropdownMenuItem onClick={() => handleMarkRead(fb.id, false)}>
                                    <MessageSquare className="mr-2 h-4 w-4" /> Mark as Unread
                                </DropdownMenuItem>
                            )} */}
                            {canDelete && (
                                <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => {
                                        setCurrentFeedback(fb);
                                        setDeleteOpen(true);
                                    }}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                title="Orders Feedback"
                description="View employees feedbacks"
            />

            {/* {isLoading && !feedbacks.length ? (
                <div className="flex justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading feedbacks...</span>
                </div>
            ) : !canRead ? (
                <p className="text-center text-muted-foreground">You don't have permission to view feedbacks.</p>
            ) : ( */}
            <DataTable
                data={feedbacks}
                totalCount={pagination.count}
                columns={columns}
                searchPlaceholder="Search by comment or employee..."
                pageIndex={page - 1}
                pageSize={pageSize}
                pageCount={pagination.count ? Math.ceil(pagination.count / pagination.page_size) : 1}
                onPageChange={(p) => setPage(p + 1)}
                onPageSizeChange={setPageSize}
                onSearch={setSearchTerm}
                isLoading={isLoading}
                showExport={false}
                showImport={false}
            />
            {/* )} */}

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Feedback?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will delete the feedback from{' '}
                            <strong>{currentFeedback?.employee_name}</strong>.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default FeedbackPage;